/**
 * RoomSettingsPanel — "Who's talking" in the group sidebar (Step 11b, SPEC section 1).
 *
 *   1. Twin circles: Sitting out / In / Leads next per twin. Each circle carries the
 *      twin's profile card (ParticipantPopover): hover on desktop, the name below md.
 *   2. Leads: teller slots per round (`tellerCount`).
 *
 * Viewers who cannot update settings see the Leads stepper disabled with one read-only
 * line. The circles stay live for anyone the controller lets pick (a collaborator who can
 * send): their taps only set or drop a local Leads next pick, never a save.
 * In the collapsed sidebar layout nothing is rendered.
 */

import { useTranslation } from 'react-i18next'

import { Text } from '@/components/ui/Text'
import { TwinCircles } from '@/components/chat/TwinCircles/TwinCircles'
import { TellerCountSelector } from '@/components/chat/TellerCountSelector/TellerCountSelector'
import { TellerPicksController, useTellerPicks } from '@/hooks/useTellerPicks'

import { ChatRoomParticipantDto, ChatRoomSettings } from '@digital-twins/shared-types'

import { ParticipantPopover } from './ParticipantPopover'

interface RoomSettingsPanelProps {
  settings: ChatRoomSettings
  participants: Array<ChatRoomParticipantDto>
  /** The room's audience, used by the profile card to load the featured twin. */
  populationId?: string | null
  /** The page's picks controller; absent on read-only surfaces (a read-only view is derived). */
  tellerPicks?: TellerPicksController
  /**
   * The profile card's Ask action. The page owns its gate (signed-in viewers of an open
   * room): Ask only prefills an @mention for the next send, so a collaborator who sees
   * the settings read-only still gets it. Absent hides it.
   */
  onAskParticipant?: (participant: ChatRoomParticipantDto) => void
  collapsed?: boolean
  canUpdateSettings?: boolean
  isSaving?: boolean
  error?: string | null
}

export function RoomSettingsPanel(props: RoomSettingsPanelProps) {
  const {
    settings,
    participants,
    populationId = null,
    tellerPicks,
    onAskParticipant,
    collapsed = false,
    canUpdateSettings = true,
    isSaving = false,
    error = null,
  } = props

  const { t } = useTranslation('app')

  const readOnlyPicks = useTellerPicks({ participants, settings, canUpdateSettings: false })

  if (collapsed) {
    return null
  }

  const controller = tellerPicks ?? readOnlyPicks
  const isReadOnly = !canUpdateSettings || !controller.canUpdateSettings
  const isCirclesDisabled = !controller.canPickTellers

  return (
    <div className="flex flex-col gap-3" data-testid="room-settings-panel">
      <Text size="sm" className="font-bold leading-[1.4] text-twin-chat-fg">
        {t('roomPane.whoIsTalking')}
      </Text>

      <TwinCircles
        participants={participants}
        states={controller.states}
        onCycle={controller.cycle}
        isDisabled={isCirclesDisabled}
        renderProfile={(participant, trigger) => (
          <ParticipantPopover
            participant={participant}
            populationId={populationId}
            onAsk={onAskParticipant ? () => onAskParticipant(participant) : undefined}
          >
            {trigger}
          </ParticipantPopover>
        )}
      />

      <TellerCountSelector
        value={controller.tellerCount.value}
        min={controller.tellerCount.min}
        max={controller.tellerCount.max}
        isDisabled={isReadOnly || controller.tellerCount.isDisabled}
        onChange={controller.setTellerCount}
      />

      {isReadOnly && (
        <Text
          size="sm"
          className="text-xs text-twin-chat-muted"
          data-testid="room-settings-read-only"
        >
          {t('roomPane.readOnly')}
        </Text>
      )}
      {isSaving && (
        <Text
          size="sm"
          className="text-xs text-twin-chat-muted"
          data-testid="room-settings-saving"
        >
          {t('roomPane.saving')}
        </Text>
      )}
      {error && (
        <Text
          size="sm"
          className="text-xs text-destructive"
          role="alert"
          data-testid="room-settings-error"
        >
          {error}
        </Text>
      )}
    </div>
  )
}
